import {
  Alert,
  Button,
  Link,
  Stack,
  TextField,
  Typography
} from '@customink/pigment-react';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import useComingSoonSubscriptionMutation from 'hooks/queries/comingSoonSubscription';
import {isValidEmail} from 'utils/validation';

export default function ComingSoonFormContent({productId}) {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const {
    isError,
    isLoading,
    isSuccess,
    mutate: createComingSoonSubscription
  } = useComingSoonSubscriptionMutation();

  const validateEmail = (value) => {
    if (!isValidEmail(value)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  };

  const handleFormSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (validateEmail(email)) {
        createComingSoonSubscription({email, styleId: productId});
      }
    },
    [createComingSoonSubscription, email, productId]
  );

  if (isSuccess) {
    return (
      <Alert severity="success">
        We&#39;ll reach out when it&#39;s available!
      </Alert>
    );
  }

  return (
    <>
      {isError && (
        <Alert severity="error">
          There was an error submitting your request. Please try again.
        </Alert>
      )}
      <form onSubmit={handleFormSubmit}>
        <Stack alignItems="flex-start" gap="0.5rem" width="100%">
          <Stack
            direction="row"
            width="100%"
            gap="0.5rem"
            alignItems="center"
            justifyContent="space-between">
            <TextField
              type="email"
              label="Email address"
              name="email"
              placeholder="Email address"
              fullWidth
              removeHelperTextSpace={!emailError}
              value={email}
              onChange={handleEmailChange}
              error={Boolean(emailError)}
              helperText={emailError}
              inputProps={{'aria-invalid': Boolean(emailError)}}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              sx={{
                flexShrink: 0,
                marginTop: !emailError ? '1.8rem' : '0',
                whiteSpace: 'nowrap'
              }}>
              {isLoading ? 'Submitting...' : 'Notify Me'}
            </Button>
          </Stack>
          <Typography
            sx={{
              color: 'rgba(0, 0, 0, 0.57)',
              fontSize: '0.8125rem',
              lineHeight: '150%'
            }}>
            By clicking submit, I acknowledge I have read and accepted the{' '}
            <Link
              href="/about/privacy"
              target="_blank"
              rel="noopener noreferrer">
              Privacy Policy
            </Link>
            .
          </Typography>
        </Stack>
      </form>
    </>
  );
}

ComingSoonFormContent.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired
};
